/**
 * User Helper Functions
 * Centralized utilities for working with user data and context
 */

import { AuthUser } from '../hooks/useAuth';

/**
 * Get organization ID from user profile with fallback
 */
export const getOrgId = (user: AuthUser | null): string => {
  if (!user) return '';
  return user?.profile?.orgId || user?.uid || '';
};

/**
 * Get user ID
 */
export const getUserId = (user: AuthUser | null): string => {
  return user?.uid || '';
};

/**
 * Get user name with multiple fallbacks
 */
export const getUserName = (user: AuthUser | null): string => {
  if (!user) return 'User';
  return user?.profile?.name || user?.displayName || user?.email || 'User';
};

/**
 * Get user role
 */
export const getUserRole = (user: AuthUser | null): 'admin' | 'manager' | 'sales_rep' | undefined => {
  return user?.profile?.role;
};

/**
 * Get complete user context for service calls
 */
export const getCurrentUserContext = (user: AuthUser | null) => ({
  userId: getUserId(user),
  orgId: getOrgId(user),
  userName: getUserName(user),
  role: getUserRole(user)
});

/**
 * Check if user has admin role
 */
export const isAdmin = (user: AuthUser | null): boolean => {
  return user?.profile?.role === 'admin';
};

/**
 * Check if user has manager or admin role
 */
export const isManagerOrAdmin = (user: AuthUser | null): boolean => {
  const role = user?.profile?.role;
  return role === 'admin' || role === 'manager';
};

